import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCreateKitAssetsMutation } from '../../../../redux/api/fymAPI';
import { CreateKitAssetItem } from '../../../../redux/api/fymAPI.types';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { studioMessages } from './studioMessages';
import { StudioActionFeedback, studioActionErrorFeedback } from './studioActionErrorText';
import { itemsCredits } from './studioRules';
import { useVideoCreditGate } from '../credits/useVideoCreditGate';

/** The id of the song files field, so a 400 `M24_NO_MASTER` can scroll to the k3 upload. */
export const STUDIO_SONG_FIELD_ID = 'studio-song-files';

function causeValue(error: unknown, key: 'code' | 'reason'): string | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const cause = (error as { m24Limit?: Record<string, unknown> }).m24Limit;
  const value = cause?.[key];
  return typeof value === 'string' ? value : null;
}

/** True for the 409 that says the same theme and variant is still being made. */
function isBusy(error: unknown): boolean {
  return causeValue(error, 'code') === 'M24_KIT_ASSET_BUSY' || causeValue(error, 'reason') === 'M24_KIT_ASSET_BUSY';
}

/** True for the 400 of a song with no usable master. */
function isNoMaster(error: unknown): boolean {
  const reason = causeValue(error, 'reason') ?? causeValue(error, 'code');
  return reason === 'M24_NO_MASTER' || reason === 'M24_MASTER_PROCESSING';
}

interface UseStudioMakeProps {
  accountId: number;
  catalogItemUuid?: string;
  /** Called after a 400 `M24_NO_MASTER`: the catalog row changed, so read it again. */
  onNoMaster: () => void;
  /** Called with the made assets (one-off publish: the YouTube switch, the "ready" toast). */
  onCreated?: (created: Array<{ uuid: string; themeId: string }>) => void;
}

/**
 * "Make it" of the Studio (design n1–n3b): one `createKitAssets` call with `batch: false`. It
 * resolves true when the server made at least one asset: the Studio then goes back to the steps,
 * and the asset shows in the "Working on" strip (design release-kit r1, owner answer 3).
 *
 * Before the call, the credits check (`useVideoCreditGate`): a balance (plan plus packs) below the
 * cost opens the credit packs at once and sends nothing. A 402 `M24_LIMIT_QUOTA` (kind
 * `videoCredit`) opens the same packs through the global upgrade prompt (`customBaseQuery`); a 402
 * of another kind and a `M24_LIMIT_FEATURE` open their own prompt there. No other error says
 * "credits": a 400 `M24_NO_MASTER` scrolls to the master upload (also a 201 where the server
 * skipped every item for the master).
 *
 * Every other answer goes into `feedback`, which the rail shows under "Make it" (never at the top
 * of the page): a 409 `M24_KIT_ASSET_BUSY` says "This is already being made."; a 400 such as
 * `M24_LYRIC_LINES_NOT_TIMED` or `M24_NO_LYRICS` shows its own server sentence; an error with no
 * `M24_*` code (a Joi 400, a 5xx, no network) shows the general sentence, and its raw text goes
 * only to the console and Sentry. `clearFeedback` removes it when the user changes a field.
 */
export function useStudioMake({ accountId, catalogItemUuid, onNoMaster, onCreated }: UseStudioMakeProps) {
  const intl = useIntl();
  const [createKitAssets, { isLoading: isMaking }] = useCreateKitAssetsMutation();
  const { canSpend } = useVideoCreditGate(accountId);
  const [madeUuids, setMadeUuids] = useState<string[] | null>(null);
  const [lastItems, setLastItems] = useState<CreateKitAssetItem[] | null>(null);
  const [feedback, setFeedback] = useState<StudioActionFeedback | null>(null);
  const [refusedNoMaster, setRefusedNoMaster] = useState(false);

  const make = useCallback(
    async (items: CreateKitAssetItem[]): Promise<boolean> => {
      if (catalogItemUuid === undefined) {
        return false;
      }
      setFeedback(null);
      setRefusedNoMaster(false);
      setLastItems(items);
      if (!canSpend(itemsCredits(items))) {
        return false;
      }
      const result = await createKitAssets({ accountId, catalogItemUuid, items, batch: false });
      if (isSuccessfulResult(result)) {
        if (result.data.created.length === 0 && result.data.busy.length > 0) {
          setFeedback({ tone: 'info', message: intl.formatMessage(studioMessages.busy) });
          return false;
        }
        if (result.data.created.length === 0 && (result.data.skipped?.length ?? 0) > 0) {
          // Every item needs the master, and the song has no ready master: no credit was used.
          setRefusedNoMaster(true);
          onNoMaster();
          document.getElementById(STUDIO_SONG_FIELD_ID)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
        setMadeUuids(result.data.created.map((asset) => asset.uuid));
        onCreated?.(result.data.created);
        return result.data.created.length > 0;
      }
      if (isBusy(result.error)) {
        setFeedback({ tone: 'info', message: intl.formatMessage(studioMessages.busy) });
        return false;
      }
      if (isNoMaster(result.error)) {
        setRefusedNoMaster(true);
        onNoMaster();
        document.getElementById(STUDIO_SONG_FIELD_ID)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      // A plan limit (402 `M24_LIMIT_QUOTA`, the credit packs; `M24_LIMIT_FEATURE`, the plan
      // prompt) opens its prompt from customBaseQuery; it needs no inline error.
      const code = causeValue(result.error, 'code');
      // A 429 `M24_AI_BUDGET_FREE_EXHAUSTED` (the Free AI budget is spent) opens the upgrade prompt
      // from customBaseQuery too; no credit was used.
      if (code === 'M24_LIMIT_QUOTA' || code === 'M24_LIMIT_FEATURE' || code === 'M24_AI_BUDGET_FREE_EXHAUSTED') {
        return false;
      }
      setFeedback(studioActionErrorFeedback(intl, 'make it', result.error));
      return false;
    },
    [accountId, canSpend, catalogItemUuid, createKitAssets, intl, onCreated, onNoMaster]
  );

  const tryAgain = useCallback(() => {
    if (lastItems !== null) {
      make(lastItems).catch(console.error);
    }
  }, [lastItems, make]);

  const reset = useCallback(() => {
    setMadeUuids(null);
    setFeedback(null);
    setRefusedNoMaster(false);
  }, []);

  const clearFeedback = useCallback(() => setFeedback(null), []);

  return { make, tryAgain, reset, clearFeedback, isMaking, madeUuids, feedback, refusedNoMaster };
}
